import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { ToppersOfferSectionBlock as Value } from "@reactivated";

// NOTE: faithful port of the legacy `offer-section-block.html`. Every classname
// is load-bearing for the `_offers-list.scss` partial.
//
// `lead_in`, `title1`, `title2`, `sub_title` were `|safe` CharBlock fields →
// dangerouslySetInnerHTML with `?? ""`. `summary` is a server-rendered
// rich-text string that already includes its `<div class="rich-text">` wrapper
// (matching the legacy `{{ value.summary }}` output, which emitted the rendered
// HTML). The `_offers-list.scss` `&__card-copy p` selector is descendant (not a
// `.rich-text` direct-child), so we inject `summary` verbatim via
// dangerouslySetInnerHTML — no thelabui RichTextBlock (which would double-wrap)
// and no extra wrapper transform (mirroring DetailsModule's `copy` handling).
//
// Images:
//   - `support_icon`: legacy was a hand-written `<img src="{{ support_icon.url }}">`
//     using the `original` rendition — NO alt, NO width/height. Reproduce a bare
//     img, gated on `v.support_icon`.
//   - `support_image`: legacy `{% image ... max-200x120 format-png %}` tag,
//     whose default alt is `default_alt_text`. Width/height omitted to match the
//     established ported-block convention.
//
// GAP: legacy `learn_more` href had a `{% if cardNum %}?pid=learnmore...{% endif %}`
// suffix, but `cardNum`/`refPage` are NEVER set in toppers, so the href is
// always plain `learn_more.url`. We use `learn_more?.url ?? ""` (always-rendered
// anchor, empty href when unset), matching the OfferModule precedent.
type IProps = Immutable<{
    value: Value;
}>;

export const OfferSectionBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const supportIcon = v.support_icon;
    const supportImage = v.support_image;
    return (
        <article className="offer-list__card">
            <div className="offer-list__card-copy">
                {supportIcon && (
                    <div className="offer-list__card-icon">
                        <img
                            src={
                                supportIcon.renditions["original"]?.url ??
                                supportIcon.url
                            }
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                )}

                <h5
                    className="offer-list__card-lead-in"
                    dangerouslySetInnerHTML={{ __html: v.lead_in ?? "" }}
                />

                <h2
                    className="offer-list__card-title offer-list__card-title--1"
                    dangerouslySetInnerHTML={{ __html: v.title1 ?? "" }}
                />
                <h3
                    className="offer-list__card-title offer-list__card-title--2"
                    dangerouslySetInnerHTML={{ __html: v.title2 ?? "" }}
                />
                <h4
                    className="offer-list__card-sub-title"
                    dangerouslySetInnerHTML={{ __html: v.sub_title ?? "" }}
                />

                <div dangerouslySetInnerHTML={{ __html: v.summary ?? "" }} />
            </div>

            <a
                href={v.learn_more?.url ?? ""}
                title={t`learn more`}
                className="offer-list__card-cta"
            >
                {t`Learn More`}
            </a>

            {supportImage && (
                <div className="offer-list__card-image">
                    <img
                        src={
                            supportImage.renditions["max-200x120|format-png"]
                                ?.url ?? supportImage.url
                        }
                        alt={supportImage.default_alt_text}
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            )}
        </article>
    );
};
